import { useState } from 'react';
import type { Item } from '../lib/types.ts';
import { ScoreBadge } from './ScoreBadge.tsx';
import { ClassificationBadge } from './ClassificationBadge.tsx';
import { api } from '../lib/api.ts';

interface Props {
  item: Item;
  onStatusChange?: (id: string, status: string) => void;
  onOpen?: (id: string) => void;
}

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() / 1000) - ts);
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtMetricValue(v: unknown): string {
  if (typeof v === 'number') {
    return v > 10000
      ? `€${(v / 1000).toFixed(0)}k`
      : v.toLocaleString('en-IE');
  }
  return String(v ?? '');
}

type Angle = { headline?: string; why_now?: string };

function parseAngles(raw: unknown): Angle[] {
  if (Array.isArray(raw)) return raw as Angle[];
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as Angle[]; } catch { /* */ }
  }
  return [];
}

function parseMetrics(raw: unknown): Record<string, unknown> | null {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as Record<string, unknown>; } catch { /* */ }
  }
  return null;
}

export function ItemCard({ item, onStatusChange, onOpen }: Props) {
  const [busy, setBusy] = useState(false);

  const angles = parseAngles(item.deep_story_angles);
  const topAngle = angles[0];
  const hasDeepDive = item.status === 'analysed' || !!item.deep_summary;
  const metrics = parseMetrics(item.key_metrics);
  const metricEntries = metrics
    ? Object.entries(metrics).filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];

  const classification = (item.deep_final_classification ?? item.triage_type ?? null) as
    'ARTICLE' | 'CHART' | 'BRIEF' | 'MONITOR' | 'NOISE' | null;

  async function act(status: string) {
    setBusy(true);
    try {
      await api.items.setStatus(item.id, status);
      onStatusChange?.(item.id, status);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #eee',
      borderRadius: 6,
      padding: '16px 20px',
      marginBottom: 12,
      cursor: onOpen ? 'pointer' : 'default',
    }} onClick={() => onOpen?.(item.id)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <ClassificationBadge classification={classification} />
        <ScoreBadge score={item.deep_final_score ?? item.triage_score} />
        <span style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {(item.source_name ?? item.source_id).replace(/_/g, ' ')}
        </span>
        <span style={{ fontSize: 11, color: '#bbb', marginLeft: 'auto' }}>
          {timeAgo(item.ingested_at)}
        </span>
      </div>

      <h3 style={{ margin: '0 0 6px', fontSize: 16, lineHeight: 1.3, color: '#111' }}>
        {topAngle?.headline ?? item.title ?? '—'}
      </h3>

      {hasDeepDive && item.deep_summary && (
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#444', lineHeight: 1.5 }}>
          {item.deep_summary}
        </p>
      )}

      {!hasDeepDive && item.triage_reason && (
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#777', lineHeight: 1.5, fontStyle: 'italic' }}>
          {item.triage_reason}
        </p>
      )}

      {hasDeepDive && topAngle?.why_now && (
        <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
          <strong>Why now:</strong> {topAngle.why_now}
        </p>
      )}

      {metricEntries.length > 0 && (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 10 }}>
          {metricEntries.slice(0, 4).map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                {k.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#222' }}>
                {fmtMetricValue(v)}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasDeepDive && angles.length > 1 && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
          <strong>Also:</strong> {angles.slice(1).map(a => a.headline).filter(Boolean).join(' · ')}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
        <button onClick={() => act('flagged')} disabled={busy} style={btnStyle('#1a6aff')}>
          Flag for editor
        </button>
        <button onClick={() => act('dismissed')} disabled={busy} style={btnStyle('#999')}>
          Dismiss
        </button>
        <button onClick={() => act('published')} disabled={busy} style={btnStyle('#27ae60')}>
          Mark published
        </button>
        {item.raw_url && (
          <a href={item.raw_url} target="_blank" rel="noopener noreferrer"
            style={{ ...btnStyle('#555'), textDecoration: 'none', display: 'inline-block' }}>
            Open source ↗
          </a>
        )}
      </div>
    </div>
  );
}

function btnStyle(bg: string): React.CSSProperties {
  return {
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '5px 12px',
    fontSize: 12,
    cursor: 'pointer',
    fontWeight: 600,
  };
}
